/**
 * #816 — measures the ItemDetail status row and applies `chipsToHide`.
 *
 * The RULE lives in statusChipFit.js and is fully tested there. This is only the
 * measurement: a ResizeObserver on the row, and each demotable chip's natural width read
 * once while it is visible.
 *
 * Why widths are cached rather than re-read every tick: a hidden chip has no width, so
 * re-measuring after hiding would report 0, the row would look like it fits, the chip
 * would come back, and it would oscillate. The natural width is captured the first time
 * a chip is seen visible and reused from then on.
 */
import * as React from 'react';
import { chipsToHide, DEMOTE_ORDER } from './statusChipFit';

/**
 * @param {boolean} enabled  false disables measurement entirely (desktop, or no item)
 * @returns {{rowRef: object, hidden: Set<string>, registerChip: Function}}
 *
 * `registerChip(key)` returns a ref callback to put on each demotable chip.
 */
export default function useStatusChipFit(enabled = true) {
  const rowRef = React.useRef(null);
  const chipRefs = React.useRef(new Map());       // key -> element
  const naturalWidths = React.useRef(new Map());  // key -> px, captured while visible
  const [hidden, setHidden] = React.useState(() => new Set());

  const registerChip = React.useCallback((key) => (el) => {
    if (el) chipRefs.current.set(key, el);
    else chipRefs.current.delete(key);
  }, []);

  const measure = React.useCallback(() => {
    const row = rowRef.current;
    if (!row) return;

    // Capture each chip's natural width the first time it is seen with one. A chip we
    // have hidden reports 0 and must not overwrite the real value.
    chipRefs.current.forEach((el, key) => {
      const w = el?.offsetWidth || 0;
      if (w > 0 && !naturalWidths.current.has(key)) naturalWidths.current.set(key, w);
    });

    const widths = {};
    DEMOTE_ORDER.forEach((key) => {
      if (chipRefs.current.has(key) && naturalWidths.current.has(key)) {
        widths[key] = naturalWidths.current.get(key);
      }
    });

    // What the demotable chips may occupy: the row minus everything that never hides.
    const fixed = Array.from(row.children).reduce((sum, child) => {
      const key = child.getAttribute?.('data-chip-key');
      return DEMOTE_ORDER.includes(key) ? sum : sum + (child.offsetWidth || 0);
    }, 0);
    const available = (row.clientWidth || 0) - fixed;

    const next = chipsToHide(available, widths);
    setHidden((prev) => {
      // Compare by content: a fresh Set every tick would re-render forever.
      if (prev.size === next.length && next.every((k) => prev.has(k))) return prev;
      return new Set(next);
    });
  }, []);

  React.useEffect(() => {
    if (!enabled) {
      setHidden((prev) => (prev.size ? new Set() : prev));
      return undefined;
    }
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(row);
    measure();
    return () => ro.disconnect();
  }, [enabled, measure]);

  // Re-measure on EVERY render, not only when the row's own box changes (#816).
  //
  // The ResizeObserver above watches the row, and the row is full-width at every phone
  // size — so after mount it never fires again. The chips, meanwhile, arrive LATER: the
  // item fetch resolves and the type chit, the verification badge and the
  // Record-verification button all render at once. The one measurement that did run
  // happened before any of them existed, with no natural widths to compare, so nothing
  // was ever hidden.
  //
  // Measured in Chromium at 360px before this: the row was 352px wide and 46px tall — two
  // lines — with every demotable chip still showing, 307px of demotable content against
  // 198px of room. The rule was correct the whole time and was never asked.
  //
  // No dependency array on purpose. `measure` calls setHidden only when the set changes
  // BY CONTENT, so a stable layout settles one render later rather than looping; and
  // hiding a chip cannot change the row's width (full-width) or the fixed children's, so
  // the rule's input is unaffected by its own output.
  React.useEffect(() => {
    if (enabled) measure();
  });

  return { rowRef, hidden, registerChip };
}
